/**
 * usePullToRefresh (A43.3) — «потянуть панель вниз от верха → HUD-спиннер →
 * reload». Native touch-листенеры на скролл-контейнере (touchmove с
 * passive:false — только так глушится нативный скролл/overscroll при
 * протяжке; React-обработчики touch пассивные) + pointer-события мыши для
 * десктопа. Тянется ТОЛЬКО от scrollTop=0; оффсет резиновый (F=0.5, кап
 * MAX_PX); отпускание за THRESH_PX → onRefresh(), спиннер до завершения
 * промиса (мин. SPIN_MIN мс, чтобы не мигал). Рендер HUD — PullHud.
 *
 * Арбитраж жестов (A43/A40): старт на слот-чипе [data-si] не взводит
 * протяжку — long-press-drag чипов живёт внутри той же скролл-зоны, и без
 * этого протяжка ехала бы всю дорогу reorder'а и запускала reload; мышь
 * дополнительно не взводится на интерактивных целях (выделение текста в
 * инпутах — не протяжка); горизонтальное намерение до взвода хоронит жест
 * (зеркало dead-флага useSwipeRight — иначе свайп-дубликат дёргал HUD).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppData } from "./useAppData";

/** Порог по срезиненному оффсету (пальцем — THRESH_PX / F). */
const THRESH_PX = 56;
const F = 0.5;
const MAX_PX = 84;
/** Оффсет HUD, пока крутится reload. */
const REST_PX = 44;
const SPIN_MIN = 400;
/** До взвода: |dx| больше и круче — намерение горизонтальное, жест мёртв. */
const VETO_X = 12;
/** Цели, где протяжка не взводится мышью (drag = выделение/контрол). */
const MOUSE_SKIP = "input,textarea,button,select,[data-si]";

export interface PullToRefresh {
  /**
   * Callback-ref на скролл-контейнер панели: узел может появиться позже
   * первого рендера (скелетон) — обычный ref-объект эффект бы не перевесил.
   */
  ref: (el: HTMLDivElement | null) => void;
  /** Резиновый оффсет HUD, px (0 — спрятан). */
  pull: number;
  /** Палец ещё ведёт — PullHud выключает транзицию. */
  pulling: boolean;
  /** reload в полёте — спиннер крутится. */
  refreshing: boolean;
}

export function usePullToRefresh(onRefresh: () => Promise<unknown>): PullToRefresh {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [pull, setPull] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const fnRef = useRef(onRefresh);
  fnRef.current = onRefresh;
  const busyRef = useRef(false);

  const finish = useCallback((raw: number) => {
    setPulling(false);
    if (raw * F < THRESH_PX || busyRef.current) { setPull(0); return; }
    busyRef.current = true;
    setRefreshing(true);
    setPull(REST_PX);
    const t0 = Date.now();
    void Promise.resolve()
      .then(() => fnRef.current())
      .catch(() => undefined)
      .then(() => {
        window.setTimeout(() => {
          busyRef.current = false;
          setRefreshing(false);
          setPull(0);
        }, Math.max(0, SPIN_MIN - (Date.now() - t0)));
      });
  }, []);

  useEffect(() => {
    const el = node;
    if (!el) return;
    let x0 = 0, y0 = 0, raw = 0, armed = false, engaged = false, dead = false;

    const start = (x: number, y: number) => {
      if (busyRef.current || el.scrollTop > 0) { armed = false; return; }
      x0 = x; y0 = y; raw = 0; armed = true; engaged = false; dead = false;
    };
    /* @returns протяжка активна (тач-вызов должен погасить нативный скролл) */
    const move = (x: number, y: number): boolean => {
      if (!armed || dead || busyRef.current) return false;
      if (el.scrollTop > 0) { armed = false; setPull(0); setPulling(false); return false; }
      raw = y - y0;
      if (!engaged) {
        const dx = Math.abs(x - x0);
        if (dx > VETO_X && dx > Math.abs(raw)) { dead = true; return false; }
        if (raw <= 0) return false;
        engaged = true;
      }
      if (raw <= 0) { setPull(0); setPulling(false); return false; }
      setPulling(true);
      setPull(Math.min(MAX_PX, raw * F));
      return true;
    };
    const end = () => {
      if (!armed) return;
      armed = false;
      if (!engaged) return;
      finish(raw);
    };

    /* Старт на слот-чипе — жест отдан long-press-drag'у (SlotGridEditor). */
    const onChip = (t: EventTarget | null) => t instanceof Element && !!t.closest("[data-si]");
    const ts = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t && !onChip(e.target)) start(t.clientX, t.clientY);
    };
    const tm = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t && move(t.clientX, t.clientY) && e.cancelable) e.preventDefault();
    };
    /* Мышь (десктоп-отладка): pointer-события с capture на контейнере. */
    const pd = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.buttons !== 1) return;
      if (e.target instanceof Element && e.target.closest(MOUSE_SKIP)) return;
      start(e.clientX, e.clientY);
    };
    const pm = (e: PointerEvent) => { if (e.pointerType === "mouse" && e.buttons === 1) move(e.clientX, e.clientY); };
    const pu = (e: PointerEvent) => { if (e.pointerType === "mouse") end(); };

    el.addEventListener("touchstart", ts, { passive: true });
    el.addEventListener("touchmove", tm, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    el.addEventListener("pointerdown", pd);
    el.addEventListener("pointermove", pm);
    el.addEventListener("pointerup", pu);
    el.addEventListener("pointerleave", pu);
    return () => {
      el.removeEventListener("touchstart", ts);
      el.removeEventListener("touchmove", tm);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
      el.removeEventListener("pointerdown", pd);
      el.removeEventListener("pointermove", pm);
      el.removeEventListener("pointerup", pu);
      el.removeEventListener("pointerleave", pu);
    };
  }, [node, finish]);

  return { ref: setNode, pull, pulling, refreshing };
}

/**
 * useAppReload — promise-обёртка над app.reload() для pull-to-refresh:
 * резолвится, когда цикл загрузки useAppData завершился (loading true→false);
 * страховочный таймаут — чтобы спиннер не завис при аномалии.
 */
export function useAppReload(): () => Promise<void> {
  const app = useAppData();
  const waiters = useRef<(() => void)[]>([]);
  const wasLoading = useRef(app.loading);
  const reloadRef = useRef(app.reload);
  reloadRef.current = app.reload;

  useEffect(() => {
    if (wasLoading.current && !app.loading) {
      const ws = waiters.current;
      waiters.current = [];
      for (const w of ws) w();
    }
    wasLoading.current = app.loading;
  }, [app.loading]);

  return useCallback(() => {
    reloadRef.current();
    return new Promise<void>((resolve) => {
      waiters.current.push(resolve);
      window.setTimeout(resolve, 10_000);
    });
  }, []);
}
